import { ALERT_BANNER_PERIOD, FREEZE_PERIOD, FREEZE_PERIOD_SLACK_MESSAGE_URL, HOLIDAY_SEASON_PERIOD } from '@/config'
import moment from 'moment'

/**
 * A regular expression pattern for ISO 8601 dates starting with `YYYY-`.
 *
 * @constant {RegExp}
 */
const ISO_8601_YEAR_PATTERN = /^\d{4}-/

/**
 * The recurring date range for the holiday season.
 *
 * @constant {Date[]}
 */
const HOLIDAY_SEASON_RANGE = HOLIDAY_SEASON_PERIOD ? Object.freeze(parsePeriod(HOLIDAY_SEASON_PERIOD)) : undefined

/**
 * A top nav bar title to be displayed during the holiday season
 * as determined by the `HOLIDAY_SEASON_RANGE` tuple.
 *
 * @constant {string}
 */
export const HOLIDAY_SEASON_GREETINGS_TITLE = 'The team wishes you happy holidays!'

/**
 * The range of dates in which the alert banner is displayed.
 *
 * @constant {Date[]}
 */
const ALERT_BANNER_PERIOD_RANGE = ALERT_BANNER_PERIOD ? Object.freeze(parsePeriod(ALERT_BANNER_PERIOD)) : undefined

/**
 * The range of dates during which policy deployments are frozen.
 *
 * @constant {Date[]}
 */
const FREEZE_PERIOD_RANGE = FREEZE_PERIOD ? Object.freeze(parsePeriod(FREEZE_PERIOD)) : undefined

/**
 * Message displayed in `FreezePeriodBanner` notifying users about changes introduced
 * during a code freeze period.
 */
export const FREEZE_PERIOD_MESSAGE = `During <i>freeze period</i>, ${toPeriodDateRange(
  FREEZE_PERIOD_RANGE
)}, you won't be able to make policy deployments (check our <a href="${FREEZE_PERIOD_SLACK_MESSAGE_URL}">Slack post</a>).`

/**
 * Renders a human-readable string describing a date range in the viewer's local time,
 * including a short UTC offset hint (e.g.: "Jul 24th 17:00 to Aug 3rd 10:00 (UTC+2)").
 *
 * @private
 * @param range A tuple describing the start and end dates of the interval.
 * @returns A human-readable string describing the period, or `undefined` if both ends are missing.
 */
function toPeriodDateRange(range?: readonly [Date, Date]): string | undefined {
  const [rangeStart, rangeEnd] = range ?? []

  const start = rangeStart ? moment(rangeStart).format('MMM Do HH:mm') : undefined
  const end = rangeEnd ? moment(rangeEnd).format('MMM Do HH:mm') : undefined

  const startOffset = rangeStart ? toUtcOffset(rangeStart) : undefined
  const endOffset = rangeEnd ? toUtcOffset(rangeEnd) : undefined

  if (start && end) {
    // Repeat the offset on each endpoint only when the range spans a UTC offset change (e.g.: a DST
    // boundary); otherwise a single shared hint reads better.
    return startOffset === endOffset
      ? `${start} to ${end} (${endOffset})`
      : `${start} (${startOffset}) to ${end} (${endOffset})`
  }

  return start ? `from ${start} (${startOffset})` : end ? `until ${end} (${endOffset})` : undefined
}

/**
 * Renders the viewer's local UTC offset for a given `date` in a short, human-readable form
 * (e.g.: "UTC", "UTC+2", "UTC-5:30").
 *
 * @private
 * @param date The date whose local UTC offset should be rendered.
 * @returns The formatted UTC offset.
 */
function toUtcOffset(date: Date): string {
  const offsetMinutes = moment(date).utcOffset()
  if (offsetMinutes === 0) {
    return 'UTC'
  }

  const sign = offsetMinutes < 0 ? '-' : '+'
  const hours = Math.floor(Math.abs(offsetMinutes) / 60)
  const minutes = Math.abs(offsetMinutes) % 60

  return `UTC${sign}${hours}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}`
}

/**
 * Parses an interval string into a date range.
 *
 * @private
 * @param interval A time interval in a `[YYYY-]MM-DD['T'HH:mm] "/" iso-8601-duration` format.
 * @returns A `Date` tuple describing the start and end dates of the period.
 */
function parsePeriod(interval: string): [Date, Date] {
  const [startDate, duration] = interval.split(/\s*\/\s*/)

  const fullStartDate = ISO_8601_YEAR_PATTERN.test(startDate) // YYYY-MM-DD
    ? startDate
    : `${new Date().getUTCFullYear()}-${startDate}`

  const intervalStart = moment.utc(fullStartDate)
  const intervalEnd = moment(intervalStart).add(moment.duration(duration)).utc()

  return [intervalStart.toDate(), intervalEnd.toDate()]
}

/**
 * Checks whether a `date` is within a range of dates.
 *
 * @private
 * @param date The date to check.
 * @param range A tuple describing the start and end dates of the interval.
 * @returns `true` if both ends of `range` are defined and `date` falls
 *  within them; `false`, in any other case.
 */
function isDateInRange(date: Date, range?: readonly [Date, Date]): boolean {
  const [rangeStart, rangeEnd] = range ?? []
  if (!rangeStart || !rangeEnd) {
    return false
  }

  return date > rangeStart && date < rangeEnd
}

/**
 * Checks whether a `date` is within the Holiday season period,
 * as described by the `VUE_APP_HOLIDAY_SEASON_PERIOD` environment variable.
 *
 * @param date The date to check (defaults to the current date).
 * @returns `true` if the given `date` falls within the Holiday season; `false`,
 *  if it doesn't or if `VUE_APP_HOLIDAY_SEASON_PERIOD` was not configured.
 */
export const isHolidaySeason = (date = new Date()) => isDateInRange(date, HOLIDAY_SEASON_RANGE)

/**
 * Checks whether the given `date` falls within the alert banner display time window.
 *
 * @param date The date to check (defaults to now if not provided).
 * @returns `true` if the given `date` falls within the alert banner display time window or if `VUE_APP_ALERT_BANNER_PERIOD` is unset; `false`, otherwise.
 */
export const isAlertBannerPeriod = (date = new Date()) =>
  ALERT_BANNER_PERIOD_RANGE === undefined || isDateInRange(date, ALERT_BANNER_PERIOD_RANGE)

/**
 * Checks whether the given `date` falls within the code freeze time window,
 * as described by the `VUE_APP_FREEZE_PERIOD` environment variable.
 *
 * @param date The date to check (defaults to now if not provided).
 * @returns `true` if the given `date` falls within the code freeze window; `false`,
 *  if it doesn't or if `VUE_APP_FREEZE_PERIOD` was not configured.
 */
export const isFreezePeriod = (date = new Date()) => isDateInRange(date, FREEZE_PERIOD_RANGE)
